import {
    useGetCabinetPreviewQuery,
    useGetRoomPreviewQuery,
} from 'components/customer/Preview3D/store/previewApi';
import {
    useGetHardwareOptionsQuery,
    useGetQFPProductStructureQuery,
} from 'components/customer/Product/store/productApi';
import {cloneDeep} from 'lodash';
import {useCallback, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {HardwareOption} from 'shared/types/HardwareOption';

export enum ICON_STATUS {
    WAIT,
    HIDE,
    SHOW,
}

export const useInformation = (cabinetType: number) => {
    const {slug, roomSlug} = useParams() as {
        slug?: string;
        roomSlug?: string;
    };
    const isPreview = !!(slug || roomSlug);

    const {data: previewData} = useGetCabinetPreviewQuery(
        {slug: slug ?? ''},
        {skip: !slug}
    );
    const {data: roomPreviewData} = useGetRoomPreviewQuery(
        {slug: roomSlug ?? ''},
        {skip: !roomSlug}
    );
    const {data, isLoading} = useGetHardwareOptionsQuery(undefined, {
        skip: isPreview,
    });
    const {data: productData} = useGetQFPProductStructureQuery(
        {
            cabinetType,
        },
        {skip: isPreview}
    );

    const {showSupplyMethods, showHardwareInclusions, showAdjustableLegs} =
        useMemo(() => {
            // Room preview has no product field structure; show icons when options exist.
            if (roomSlug) {
                return {
                    showSupplyMethods: ICON_STATUS.SHOW,
                    showHardwareInclusions: ICON_STATUS.SHOW,
                    showAdjustableLegs: ICON_STATUS.SHOW,
                };
            }

            const structureData = slug
                ? previewData?.product_config?.structure
                : productData?.originalStructure;
            if (structureData) {
                let showSupplyMethods = ICON_STATUS.HIDE;
                let showHardwareInclusions = ICON_STATUS.HIDE;
                let showAdjustableLegs = ICON_STATUS.HIDE;

                structureData.forEach((structure) => {
                    if (structure.name === 'Specs') {
                        structure.fieldsets.forEach((fieldset) => {
                            if (fieldset.name === 'additional') {
                                fieldset.fields.forEach((field) => {
                                    if (
                                        field.name ===
                                        'cabinet_include_assembly'
                                    ) {
                                        if (field?.options?.visible == 0)
                                            return;

                                        showSupplyMethods = ICON_STATUS.SHOW;
                                    }

                                    if (
                                        field.name ===
                                        'cabinet_include_hardware'
                                    ) {
                                        if (field?.options?.visible == 0)
                                            return;

                                        showHardwareInclusions =
                                            ICON_STATUS.SHOW;
                                    }

                                    if (
                                        field.name === 'cabinet_adjustable_legs'
                                    ) {
                                        if (field?.options?.visible == 0)
                                            return;

                                        showAdjustableLegs = ICON_STATUS.SHOW;
                                    }
                                });
                            }
                        });
                    }
                });

                return {
                    showSupplyMethods,
                    showHardwareInclusions,
                    showAdjustableLegs,
                };
            } else {
                return {
                    showSupplyMethods: ICON_STATUS.WAIT,
                    showHardwareInclusions: ICON_STATUS.WAIT,
                    showAdjustableLegs: ICON_STATUS.WAIT,
                };
            }
        }, [productData, previewData, roomSlug, slug]);

    const mapImage = useCallback((hardwareOption: HardwareOption) => {
        const option = cloneDeep(hardwareOption);
        if (option.adjLegs) {
            if (option.value == 1) {
                option.image = 'Feet.svg';
                option.customImage = true;
            }

            if (option.value == 0) {
                option.image = 'No_feet.svg';
                option.customImage = true;
            }
        }

        return option;
    }, []);

    const {productSupplyMethods, hardwareInclusions, adjustableLegs} =
        useMemo(() => {
            let productSupplyMethods: HardwareOption[] = [];
            let hardwareInclusions: HardwareOption[] = [];
            let adjustableLegs: HardwareOption[] = [];

            const hardwareData = roomSlug
                ? roomPreviewData?.hardware_options
                : slug
                  ? previewData?.hardware_options
                  : data;

            if (hardwareData) {
                productSupplyMethods = hardwareData.filter(
                    (item) => item.assembly
                );
                hardwareInclusions = hardwareData.filter(
                    (item) => item.supplyHardware
                );
                adjustableLegs = hardwareData
                    .map(mapImage)
                    .filter((item) => item.adjLegs);
            }

            return {
                productSupplyMethods,
                hardwareInclusions,
                adjustableLegs,
            };
        }, [data, previewData, roomPreviewData, roomSlug, slug, mapImage]);

    return {
        isLoading: isPreview ? false : isLoading,
        productSupplyMethods,
        hardwareInclusions,
        adjustableLegs,
        showSupplyMethods,
        showHardwareInclusions,
        showAdjustableLegs,
    };
};
